// État : Bilan (lecture seule, temps réel) — situation à une date d'arrêté.
// Deux colonnes : Actif (ce que je possède) / Passif (comment je suis financé).
// Rubriques structurées avec sous-totaux ; le résultat de l'exercice (calculé
// depuis les écritures) est repris automatiquement dans les capitaux propres.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { telechargerExport } from "@/lib/exportFichier";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { Scale, Download, Printer, Loader2, CheckCircle2, AlertTriangle, FileText } from "lucide-react";

const anneeCourante = new Date().getFullYear();
// Montant XPF sans décimale ; valeurs négatives entre parenthèses (perte).
const fmt = (n) => {
  const v = Math.round(n || 0);
  return v < 0 ? `(${Math.abs(v).toLocaleString("fr-FR")})` : v.toLocaleString("fr-FR");
};

function Colonne({ titre, sousTitre, couleur, rubriques, total, libelleTotal, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden flex flex-col">
      <div className={`${couleur} text-white px-4 py-3`}>
        <p className="font-bold text-sm tracking-wide">{titre}</p>
        <p className="text-xs text-white/70">{sousTitre}</p>
      </div>
      <div className="flex-1 divide-y divide-[#4A8A8A]/10">
        {rubriques.map((r, ri) => (
          <div key={ri} className="py-1">
            <p className="px-4 pt-2 pb-1 text-xs font-bold uppercase tracking-wide text-[#4A8A8A]">{r.titre}</p>
            {r.postes.length === 0 ? (
              <p className="px-4 pb-1 text-sm text-[#1D3D3D]/40">—</p>
            ) : r.postes.map((p) => (
              <div key={p.cle} className="flex items-center justify-between px-4 py-1 text-sm hover:bg-[#E8F4F4]/40">
                <span className={`text-[#1D3D3D] ${p.cle === "resultat" ? "font-medium" : ""}`}>
                  {p.libelle}
                  {p.cle === "resultat" && (
                    <span className="ml-1 text-xs text-[#1D3D3D]/50">({p.montant >= 0 ? "bénéfice" : "perte"})</span>
                  )}
                </span>
                <span data-testid={p.cle === "resultat" ? "bilan-resultat" : undefined}
                  className={`font-medium tabular-nums ${p.montant < 0 ? "text-[#8A4A4A]" : "text-[#1D3D3D]"}`}>
                  {fmt(p.montant)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between px-4 py-1.5 bg-[#E8F4F4]/60 text-sm font-semibold text-[#1D3D3D]">
              <span>Sous-total {r.titre}</span>
              <span className="tabular-nums">{fmt(r.sous_total)}</span>
            </div>
          </div>
        ))}
      </div>
      <div className={`${couleur} text-white px-4 py-3 flex items-center justify-between font-bold`}>
        <span>{libelleTotal}</span>
        <span data-testid={`${testid}-total`} className="tabular-nums text-lg">{fmt(total)}</span>
      </div>
    </div>
  );
}

export default function Bilan() {
  const { company } = useAuth();
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [dateArrete, setDateArrete] = useState("");
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const params = useCallback(() => ({
    exercice: parseInt(exercice, 10),
    date_arrete: dateArrete || undefined,
  }), [exercice, dateArrete]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/etats/bilan", { params: params() });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [params]);

  useEffect(() => { charger(); }, [charger]);

  const exporter = async (format) => {
    try {
      await telechargerExport("/etats/bilan/export", params(), format, `bilan_${exercice}`);
    } catch {
      toast.error("L'export a échoué. Veuillez réessayer.");
    }
  };

  return (
    <div className="fenua-fade-up" data-testid="page-bilan">
      <div className="no-print flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <Scale className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Bilan</h1>
            <p className="text-sm text-[#1D3D3D]/70">Ce que l'entreprise possède et comment elle est financée, à une date donnée.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" data-testid="export-csv" onClick={() => exporter("csv")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="secondary" data-testid="export-xls" onClick={() => exporter("xls")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> XLS
          </Button>
          <Button variant="secondary" data-testid="export-pdf" onClick={() => exporter("pdf")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button data-testid="bouton-imprimer" onClick={() => window.print()}
            className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            <Printer className="h-4 w-4 mr-1" /> Imprimer
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="no-print mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Exercice</Label>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="filtre-exercice" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Situation au (date d'arrêté)</Label>
          <DatePicker testId="filtre-date-arrete" value={dateArrete} onChange={setDateArrete} />
        </div>
      </div>

      {/* Contenu imprimable */}
      <div className="mt-6 zone-impression">
        <div className="bg-white border border-[#4A8A8A]/20 rounded-lg px-5 py-3 mb-4">
          <p className="font-bold text-[#1D3D3D]">{company?.raison_sociale}</p>
          <p className="text-xs text-[#1D3D3D]/60">
            Bilan — Exercice {exercice}{dateArrete ? ` · arrêté au ${dateArrete}` : ""}
          </p>
        </div>

        {chargement ? (
          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : !data ? (
          <p className="bg-white border border-[#4A8A8A]/20 rounded-lg p-10 text-center text-sm text-[#1D3D3D]/60">Aucune donnée pour ces critères.</p>
        ) : (
          <div className="space-y-4">
            <div className="bilan-colonnes grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <Colonne testid="colonne-actif" titre="ACTIF" sousTitre="Ce que je possède"
                couleur="bg-[#2D5A5A]" rubriques={data.actif} total={data.total_actif} libelleTotal="TOTAL ACTIF" />
              <Colonne testid="colonne-passif" titre="PASSIF" sousTitre="Comment je suis financé"
                couleur="bg-[#1D3D3D]" rubriques={data.passif} total={data.total_passif} libelleTotal="TOTAL PASSIF" />
            </div>

            <div data-testid="bandeau-equilibre" className={`rounded-lg px-5 py-4 flex items-center gap-3 ${data.equilibre ? "bg-[#E8F4F4] text-[#1D3D3D]" : "bg-red-50 text-red-800 border border-red-200"}`}>
              {data.equilibre ? <CheckCircle2 className="h-5 w-5 text-[#2D5A5A]" /> : <AlertTriangle className="h-5 w-5 text-red-600" />}
              {data.equilibre ? (
                <p className="text-sm font-semibold">Bilan équilibré — Actif = Passif = {fmt(data.total_actif)} XPF.</p>
              ) : (
                <p className="text-sm font-semibold">
                  Écart : {fmt(Math.abs(data.ecart))} XPF. Vérifiez vos écritures.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
